/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

import IcalFreq from '../ical-freq';

// TODO Rekurzivní kopírování kalendářů
// - Rekurzivně procházet všechny subkomponenty a jejich vlastní subkomponenty atd.
// - Nyní se kopíruje jen první úroveň komponent, což bude pro aktuální potřeby stačit.
// TODO Strict mód
// - Možnost volby co dělat, pokud se narazí na nepodporovanou událost:
//   ignorovat, zalogovat chybu a pokračovat, zastavit slice.
//   Nyní se vždy ignoruje.
// - Pravděpodobně by šlo pomocí dvojice parametrů:
//   - strict: boolean - vyhodit chybu a zastavit slice u nepodporovaných
//   - errorHandler: function - pokud by byl vyplněný a byl by strict false,
//     tak sem by byly předávány chyby, které by jinak vyhodil slice
// TODO Dokonalý ořez kalendářů
// - Zaručovat, že všechny výskyty událostí v ořezaném kalendáři leží
//   na intervalu na vstupu.
//   Nebylo by tak nutné dodatečně procházet neplatné výskyty.
// - Algoritmus by musel prodporovat všechny typy opakování.
//   U některých typů opakování není vůbec jisté, jestli lze s rozumným výkonem
//   tohoto požadavku dosáhnout.
/**
 * Ořezávač kalendářů ve formátu iCalendar
 *
 * Slouží především pro optimalizaci výkonu při iteraci přes výskyty opakovaných
 * událostí, které začali v daleké minulosti.
 * Také vyřazuje události, u kterých je jasné, že do intervalu nepatří.
 * Opět se tak zlepší výkon, protože přes ně není nutné iterovat.
 *
 * Hlavní myšlenkou je neřešit detaily opakování a spíše se snažit jednoduše
 * najít nejhorší případ, kdy by událost mohla začínat nebo končit.
 *
 * Důležité je při ořezu zachovat umístění DTSTART vzhledem k intervalu
 * opakování, což je důsledek následující požadavků v RFC 5545:
 * - 3.8.5.1. a 3.8.5.2.: The "DTSTART" property value SHOULD match
 *   the pattern of the recurrence rule, if specified.
 * - 3.8.5.3.: The "DTSTART" property value SHOULD be synchronized with
 *   the recurrence rule, if specified.  The recurrence set generated
 *   with a "DTSTART" property value not synchronized with the recurrence rule
 *   is undefined.
 * - 3.3.10.: Information, not contained in the rule, necessary to determine
 *   the various recurrence instance start time and dates are derived
 *   from the Start Time ("DTSTART") component attribute.
 */
class IcalSlicer {
  /**
   * @param {IcalApi} ICAL
   * @param {IcalTimeUtil} timeUtil
   * @param {IcalUtil} util
   */
  constructor(ICAL, timeUtil, util) {
    this.ICAL = ICAL;
    this.timeUtil = timeUtil;
    this.util = util;
  }

  /**
   * Ořeže kalendář tak, že vybere a případně upraví události, aby ve výsledném
   * ořezaném kalendáři byly pouze události, které se vyskytují v intervalu
   * `<startDate, endDate)`.
   *
   * Funguje podobně jako standardní metoda `Array.prototype.slice`.
   * Akorát se neořezává pole s položkami, ale kalendář s událostmi a
   * také se neořezává dle indexů položek, ale dle datumů událostí.
   * Stejně jako standardní `slice` je tato metoda imutabilní, kalendář
   * na vstupu se neupravuje, na výstupu je vždy vytvořena jeho kopie.
   * Pořadí komponent a jejich vlastností na vstupu je ve výstupu zachováno.
   *
   * Hlavní prioritou této metody je výkon, nikoliv přesnost.
   * Je zaručeno pouze to, že se neodřízne událost, která je v intervalu.
   * Není zaručeno, že se odříznou všechny události, které v intervalu nejsou.
   *
   * Ořezávač zatím nepodporuje všechny typy opakování, pokud na takovou událost
   * narazí, zkopíruje ji na výstup beze změny.
   *
   * Dále zatím není podporováno rekurzivní kopírování kalendáře na vstupu.
   * Dle specifikace je povoleno, aby v sobě bylo zanořeno více komponent.
   * Nyní se kopírují pouze všechny vlastnosti z hlavní komponenty kalendáře,
   * všechny komponenty, které jsou přímými potomky hlavní komponenty
   * a všechny vlastnosti těchto komponent potomků.
   * @param {IcalApi.Component} calendar Komponenta kalendáře, která
   * se má ořezat.
   * @param {Object} [opts={}]
   * @param {IcalApi.Time} [opts.endDate] Konec intervalu, ve kterém
   * se ořezávají události.
   * @param {IcalApi.Time} [opts.startDate] Začátek intervalu, ve kterém
   * se ořezávají události.
   * @returns {IcalApi.Component} Ořezaný kalendář, u kterého není zaručeno,
   * že obsahuje nějaké komponenty tak, jak vyžaduje iCal specifikace.
   * Pokud se na vstup umístí prázný kalendář, nebo pokud jsou všechny události
   * ořezány, tak bude výsledkem prázdný kalendář.
   */
  slice(calendar, { endDate, startDate } = {}) {
    const { ICAL, util } = this;
    // Vytvoří se nový výstupní kalendář.
    const destCalendar = new ICAL.Component('vcalendar');
    // Nakopírují se do něj všechny vlastnosti vstupního beze změny.
    calendar.getAllProperties().forEach((property) => {
      const destProperty = util.cloneProperty(property);
      destCalendar.addProperty(destProperty);
    });
    // Předem se vytvoří a propojí všechny události ve vstupním kalendáři,
    // protože jsou informace v nich obsažené nunté při ořezu.
    const events = util.getAllEvents(calendar);
    // Komponenty ze vstupního kalendáře se projdou a buď se přeskočí,
    // nebo se s případnými změnami nakopírují do výstupního kalendáře.
    calendar.getAllSubcomponents().forEach((component) => {
      const destComponent = this.sliceComponent(component, events, {
        endDate,
        startDate
      });
      if (!isNil(destComponent)) {
        destCalendar.addSubcomponent(destComponent);
      }
    });
    // Vrátí se výstupní kalendář.
    return destCalendar;
  }

  /**
   * Ořeže komponentu kalendáře na první úrovni.
   * @param {IcalApi.Component} component Komponenta, která se má ořezat.
   * @param {Array.<IcalApi.Event>} events Seznam všech rodičovských událostí
   * s navázanými výjimkami.
   * @param {Object} [opts={}]
   * @param {IcalApi.Time} [opts.endDate] Konec intervalu, ve kterém
   * se ořezávají události.
   * @param {IcalApi.Time} [opts.startDate] Začátek intervalu, ve kterém
   * se ořezávají události.
   * @private
   * @returns {IcalApi.Component|null} Ořezaná komponenta nebo `null`,
   * pokud se komponenta nemá do výsledku zahrnout.
   */
  sliceComponent(component, events, { endDate, startDate } = {}) {
    const { util } = this;
    // Komponenty s událostmi se ořezávají.
    if (component.name === 'vevent') {
      // Ke komponentě se nalezne odpovídající událost s navázanými výjimkami.
      // TODO Optimalizovat výkon, předem připravit mapu událostí dle komponnet.
      const foundEvent = events.find((event) => event.component === component);
      // Pokud se událost nepodařilo nalézt, znamená to,
      // že jde o výjimku z opakování.
      // V tom případě se komponenta nakopíruje na výstup beze změny.
      // TODO Podporovat výjimky z opakování.
      if (isNil(foundEvent)) {
        return util.cloneComponent(component);
      }
      // Rodičovká událost se ořízne.
      return this.sliceEvent(foundEvent, { endDate, startDate });
    }
    // Ostatní komponenty se nakopírují beze změny.
    return util.cloneComponent(component);
  }

  /**
   * Ořeže datum začátku události s denním opakováním.
   * @param {IcalApi.Event} event Událost, která se má ořezat.
   * @param {IcalApi.Recur} recur Informace o opakování události, které
   * se mají ořezat.
   * @param {IcalApi.Time} startDate Začátek intervalu, ve kterém se ořezávají
   * události.
   * @private
   * @returns {IcalApi.Time|null} Ořezané datum začátku nebo `null`,
   * pokud se událost má do výsledku zahrnout beze změny.
   */
  sliceDailyStartDate(event, recur, startDate) {
    const { ICAL } = this;
    // TODO Podporovat INTERVAL a COUNT.
    if (recur.interval !== 1 || !isNil(recur.count)) {
      return null;
    }
    // TODO Podporovat BY* pravidla.
    const { parts } = recur;
    if (Object.keys(parts).length) {
      return null;
    }
    // Od začátku hledaného intervalu se odečte doba události.
    // To je nejhorší případ začátku události,
    // která by do intervalu ještě patřila.
    const iterationDate = this.timeUtil.adjust(startDate, {
      seconds: -1 * event.duration.toSeconds()
    });
    // Z nalezeného datumu začátku se použije rok, měsíc a den.
    // Časové hodnoty se zachovají, ty se ve výskytech nesmí změnit.
    const eventStartDate = event.startDate;
    const slicedDate = new ICAL.Time({
      year: iterationDate.year,
      month: iterationDate.month,
      day: iterationDate.day,
      hour: eventStartDate.hour,
      minute: eventStartDate.minute,
      second: eventStartDate.second
    });
    // Ořezané datum nesmí být dříve, než skutečné datum začátku opakovaní.
    if (eventStartDate.toUnixTime() > slicedDate.toUnixTime()) {
      return null;
    }
    return slicedDate;
  }

  /**
   * Ořeže vlastnost DTEND.
   * @param {IcalApi.Event} event Událost, které tato vlasnost patří.
   * @param {IcalApi.Property} property Vlastnost, která se má ořezat.
   * @param {IcalApi.Time} slicedStartDate Ořezané datum začátku události.
   * @private
   * @returns {IcalApi.Property}
   */
  sliceDtend(event, property, slicedStartDate) {
    const { ICAL } = this;
    // Vytvoří se nová výstupní vlastnost.
    const destProperty = new ICAL.Property(property.name);
    // Hodnota se jí nastaví tak, že se vezme ořezaný začátek
    // a k němu se přičte doba trvání události.
    const destValue = this.timeUtil.adjust(slicedStartDate, {
      seconds: event.duration.toSeconds()
    });
    destProperty.setValue(destValue);
    return destProperty;
  }

  /**
   * Ořeže vlastnost DTSTART.
   * @param {IcalApi.Property} property Vlastnost, která se má ořezat.
   * @param {IcalApi.Time} slicedStartDate Ořezané datum začátku události.
   * @private
   * @returns {IcalApi.Property}
   */
  sliceDtstart(property, slicedStartDate) {
    const { ICAL } = this;
    // Vytvoří se nová výstupní vlastnost.
    const destProperty = new ICAL.Property(property.name);
    // Hodnota se nastaví na ořezaný začátek události.
    destProperty.setValue(slicedStartDate);
    return destProperty;
  }

  /**
   * Ořeže komponentu s událostí.
   * @param {IcalApi.Event} event Událost, která se má ořezat.
   * @param {Object} [opts={}]
   * @param {IcalApi.Time} [opts.endDate] Konec intervalu, ve kterém
   * se ořezávají události.
   * @param {IcalApi.Time} [opts.startDate] Začátek intervalu, ve kterém
   * se ořezávají události.
   * @private
   * @returns {IcalApi.Component|null} Ořezaná komponenta nebo `null`,
   * pokud se událost nemá do výsledku zahrnout.
   */
  sliceEvent(event, { endDate, startDate } = {}) {
    const { util } = this;
    const { component } = event;
    // Rodičovké události, které mají výjimky se kopírují beze změny.
    // TODO Podporovat výjimky z opakování.
    if (Object.keys(event.exceptions).length) {
      return util.cloneComponent(component);
    }
    // Ořez opakované události.
    if (event.isRecurring()) {
      return this.sliceRecurringEvent(event, { endDate, startDate });
    }
    // Pokud událost není výjimkou a ani opakovanou událostí,
    // tak jde o událost bez opakování.
    return this.sliceOneTimeEvent(event, { endDate, startDate });
  }

  /**
   * Ořeže datum začátku události s měsíčním opakováním.
   * @param {IcalApi.Event} event Událost, která se má ořezat.
   * @param {IcalApi.Recur} recur Informace o opakování události, které
   * se mají ořezat.
   * @param {IcalApi.Time} startDate Začátek intervalu, ve kterém se ořezávají
   * události.
   * @private
   * @returns {IcalApi.Time|null} Ořezané datum začátku nebo `null`,
   * pokud se událost má do výsledku zahrnout beze změny.
   */
  sliceMonthlyStartDate(event, recur, startDate) {
    // TODO Podporovat INTERVAL a COUNT.
    if (recur.interval !== 1 || !isNil(recur.count)) {
      return null;
    }
    // TODO Podporovat i ostatní BY* pravidla než jen BYMONTHDAY.
    // TODO Podporovat i negativní hodnoty (-1 = poslední den v měsíci).
    if (Object.keys(recur.parts).length !== 1) {
      return null;
    }
    const values = this.util.getByPartValues('BYMONTHDAY', recur.parts);
    if (values.length !== 1 || values[0] < 0) {
      return null;
    }
    // Od začátku hledaného intervalu se odečte doba události.
    // To je nejhorší případ začátku události,
    // která by do intervalu ještě patřila.
    const iterationDate = this.timeUtil.adjust(startDate, {
      seconds: -1 * event.duration.toSeconds()
    });
    // Následně je nutné se vracet v minulosti, dokud se nenalezne měsíc,
    // který obsahuje den v měsíci, podle kterého se opakuje.
    // Toto je nutné řešit pro 29. až 31. den v měsíci, které se nevyskytují
    // v každém měsíci.
    const eventStartDate = event.startDate;
    const day = eventStartDate.day;
    let { year, month } = iterationDate;
    while (this.ICAL.Time.daysInMonth(month, year) < day) {
      if (month === 1) {
        year -= 1;
        month = 12;
      } else {
        month -= 1;
      }
    }
    // Z nalezeného datumu začátku se použije rok, měsíc a den.
    // Časové hodnoty se zachovají, ty se ve výskytech nesmí změnit.
    const slicedDate = new this.ICAL.Time({
      year,
      month,
      day,
      hour: eventStartDate.hour,
      minute: eventStartDate.minute,
      second: eventStartDate.second
    });
    // Ořezané datum nesmí být dříve, než skutečné datum začátku opakovaní.
    if (eventStartDate.toUnixTime() > slicedDate.toUnixTime()) {
      return null;
    }
    return slicedDate;
  }

  /**
   * Ořeže komponentu s událostí bez opakování.
   * @param {IcalApi.Event} event Událost, která se má ořezat.
   * @param {Object} [opts={}]
   * @param {IcalApi.Time} [opts.endDate] Konec intervalu, ve kterém
   * se ořezávají události.
   * @param {IcalApi.Time} [opts.startDate] Začátek intervalu, ve kterém
   * se ořezávají události.
   * @private
   * @returns {IcalApi.Component|null} Ořezaná komponenta nebo `null`,
   * pokud se událost nemá do výsledku zahrnout.
   */
  sliceOneTimeEvent(event, { endDate, startDate } = {}) {
    const { util } = this;
    // Pokud leží událost mimo hledaný interval, zahodí se.
    if (!util.isEventWithinRange(event, { endDate, startDate })) {
      return null;
    }
    // Pokud je událost v intervalu, zahrne se její kopie beze změn.
    return util.cloneComponent(event.component);
  }

  /**
   * Ořeže komponentu s událostí s opakováním.
   * @param {IcalApi.Event} event Událost, která se má ořezat.
   * @param {Object} [opts={}]
   * @param {IcalApi.Time} [opts.endDate] Konec intervalu, ve kterém
   * se ořezávají události.
   * @param {IcalApi.Time} [opts.startDate] Začátek intervalu, ve kterém
   * se ořezávají události.
   * @private
   * @returns {IcalApi.Component|null} Ořezaná komponenta nebo `null`,
   * pokud se událost nemá do výsledku zahrnout.
   */
  sliceRecurringEvent(event, { endDate, startDate } = {}) {
    // Nejprve se provede validace, že je ořez této události podporován.
    const { ICAL, util } = this;
    const { component } = event;
    // TODO Podporovat RDATE.
    if (component.hasProperty('rdate')) {
      return util.cloneComponent(component);
    }
    // TODO Podporovat více RRULE.
    const rrules = component.getAllProperties('rrule');
    if (rrules.length !== 1) {
      return util.cloneComponent(component);
    }
    const rrule = rrules[0];
    const recur = rrule.getFirstValue('rrule');
    // Následně se zahodí komponenty událostí, které nespadají do intervalu.
    if (
      !util.isRecurringEventWithinRange(event, recur, { endDate, startDate })
    ) {
      return null;
    }
    // Opakované události se ořezávají pouze od začátku události.
    // Pokud není začátek intervalu stanoven, není možné ořez provést.
    if (isNil(startDate)) {
      return util.cloneComponent(component);
    }
    // Určí se nový ořezaný začátek události.
    // Pokud se takový nepodaří nalézt, tak se událost zkopíruje beze změny.
    // Toto datum je nutné předpočítat dopředu, protože je potřeba pro určení,
    // jak tuto událost ořezat a následně ve více jejích vlastnostech.
    const slicedStartDate = this.sliceStartDate(event, recur, startDate);
    if (isNil(slicedStartDate)) {
      return util.cloneComponent(component);
    }
    // Vytvoří se nová výstupní událost.
    const destEvent = new ICAL.Component('vevent');
    // Postupně se ořežou její vlastnosti.
    component.getAllProperties().forEach((property) => {
      const destProperty = this.sliceRecurringProperty(
        event,
        property,
        slicedStartDate
      );
      destEvent.addProperty(destProperty);
    });
    // Vrátí se výstupní událost.
    return destEvent;
  }

  /**
   * Ořeže vlastnost události s opakováním.
   * @param {IcalApi.Event} event Událost, které tato vlasnost patří.
   * @param {IcalApi.Property} property Vlastnost, která se má ořezat.
   * @param {IcalApi.Time} slicedStartDate Ořezané datum začátku události.
   * @private
   * @returns {IcalApi.Property}
   */
  sliceRecurringProperty(event, property, slicedStartDate) {
    // Je nutné zachovat formát vstupu a překopírování všech (i neznámých)
    // vlastností.
    // Událost může být definována těmito způsoby:
    // - pouze DTSTART => upraví se jen ten, nic dalšího se nepřidává
    // - DTSTART + DURATION => upraví se jen ten DTSTART, protože DURATION
    //   v tomto případě není nutné měnit
    // - DTSTART + DTEND => upraví se oboje
    const { name } = property;
    switch (name) {
      case 'dtend':
        return this.sliceDtend(event, property, slicedStartDate);
      case 'dtstart':
        return this.sliceDtstart(property, slicedStartDate);
      default:
        return this.util.cloneProperty(property);
    }
  }

  /**
   * Ořeže datum začátku události s opakováním.
   * @param {IcalApi.Event} event Událost, která se má ořezat.
   * @param {IcalApi.Recur} recur Informace o opakování události, které
   * se mají ořezat.
   * @param {IcalApi.Time} startDate Začátek intervalu, ve kterém se ořezávají
   * události.
   * @private
   * @returns {IcalApi.Time|null} Ořezané datum začátku nebo `null`,
   * pokud se událost má do výsledku zahrnout beze změny.
   */
  sliceStartDate(event, recur, startDate) {
    // Ořežou se jen poporované typy opakování, každý svým vlastním algoritmem.
    // Ostatní typy se nakopírují beze změny.
    switch (recur.freq) {
      case IcalFreq.Daily:
        return this.sliceDailyStartDate(event, recur, startDate);
      case IcalFreq.Weekly:
        return this.sliceWeeklyStartDate(event, recur, startDate);
      case IcalFreq.Monthly:
        return this.sliceMonthlyStartDate(event, recur, startDate);
      default:
        return null;
    }
  }

  /**
   * Ořeže datum začátku události s týdenním opakováním.
   * @param {IcalApi.Event} event Událost, která se má ořezat.
   * @param {IcalApi.Recur} recur Informace o opakování události, které
   * se mají ořezat.
   * @param {IcalApi.Time} startDate Začátek intervalu, ve kterém se ořezávají
   * události.
   * @private
   * @returns {IcalApi.Time|null} Ořezané datum začátku nebo `null`,
   * pokud se událost má do výsledku zahrnout beze změny.
   */
  sliceWeeklyStartDate(event, recur, startDate) {
    const { ICAL } = this;
    // TODO Podporovat INTERVAL a COUNT.
    if (recur.interval !== 1 || !isNil(recur.count)) {
      return null;
    }
    // TODO Podporovat i ostatní BY* pravidla než jen BYDAY.
    const { parts } = recur;
    if (Object.keys(parts).length !== 1) {
      return null;
    }
    const values = this.util.getByPartValues('BYDAY', parts);
    if (!values.length) {
      return null;
    }
    // Od začátku hledaného intervalu se odečte doba události.
    // To je nejhorší případ začátku události,
    // která by do intervalu ještě patřila.
    let iterationDate = this.timeUtil.adjust(startDate, {
      seconds: -1 * event.duration.toSeconds()
    });
    // Následně je nutné se vracet v minulosti, dokud se nenalezne den v týdnu,
    // který je stejný, jako má začátek opakování, podle kterého se opakuje.
    while (iterationDate.dayOfWeek() !== event.startDate.dayOfWeek()) {
      iterationDate = this.timeUtil.adjust(iterationDate, { days: -1 });
    }
    // Z nalezeného datumu začátku se použije rok, měsíc a den.
    // Časové hodnoty se zachovají, ty se ve výskytech nesmí změnit.
    const eventStartDate = event.startDate;
    const slicedDate = new ICAL.Time({
      year: iterationDate.year,
      month: iterationDate.month,
      day: iterationDate.day,
      hour: eventStartDate.hour,
      minute: eventStartDate.minute,
      second: eventStartDate.second
    });
    // Ořezané datum nesmí být dříve, než skutečné datum začátku opakovaní.
    if (eventStartDate.toUnixTime() > slicedDate.toUnixTime()) {
      return null;
    }
    return slicedDate;
  }
}

export default IcalSlicer;
